/* Public review rendering. All customer text is inserted with textContent. */
(() => {
  'use strict';
  if (customElements.get('n-reviews')) return;
  const cache = new Map();
  const locale = document.documentElement.lang || 'de-DE';
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const countFormat = new Intl.NumberFormat(locale);
  const interpolate = (template, values = {}) => String(template || '').replace(/__([A-Z_]+)__/g, (_, key) => String(values[key.toLowerCase()] ?? ''));
  const isObject = value => value && typeof value === 'object' && !Array.isArray(value);
  const safeImage = value => {
    if (typeof value !== 'string' || /[\x00-\x1F]/.test(value)) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch (_) { return null; }
  };
  function validate(documentData) {
    if (!isObject(documentData) || documentData.schema_version !== 1 || !isObject(documentData.products)) throw new Error('Invalid review schema');
    if (Object.hasOwn(documentData, 'source_confirmed') && documentData.source_confirmed !== true) throw new Error('Unconfirmed review source');
    for (const [productId, group] of Object.entries(documentData.products)) {
      if (!/^[1-9]\d*$/.test(productId) || !isObject(group) || !Array.isArray(group.reviews) || !group.reviews.length) throw new Error('Invalid product group');
      const ids = new Set();
      for (const review of group.reviews) {
        if (!isObject(review) || review.product_id !== productId || typeof review.id !== 'string' || !review.id.trim() || ids.has(review.id)) throw new Error('Invalid review identity');
        if (typeof review.rating !== 'number' || !Number.isFinite(review.rating) || review.rating < 1 || review.rating > 5) throw new Error('Invalid rating');
        if (typeof review.name !== 'string' || !review.name.trim() || typeof review.body !== 'string' || !review.body.trim()) throw new Error('Missing original review text');
        if (typeof review.verified_purchase !== 'boolean' || !Array.isArray(review.images) || review.images.some(url => !safeImage(url))) throw new Error('Invalid review metadata');
        if (['title', 'date'].some(field => review[field] != null && typeof review[field] !== 'string')) throw new Error('Invalid review text');
        ids.add(review.id);
      }
      const actualAverage = group.reviews.reduce((sum, review) => sum + review.rating, 0) / group.reviews.length;
      if (group.count !== group.reviews.length || typeof group.average !== 'number' || !Number.isFinite(group.average) || Math.abs(group.average - actualAverage) > 1e-10) throw new Error('Review totals mismatch');
    }
    return documentData;
  }
  function load(source) {
    if (!source) return Promise.reject(new Error('Missing review asset'));
    if (!cache.has(source)) cache.set(source, fetch(source, { credentials: 'same-origin' }).then(response => {
      if (!response.ok) throw new Error('Review asset unavailable');
      return response.json();
    }).then(validate));
    return cache.get(source);
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }
  function stars(value, labels = {}) {
    const wrapper = element('span', 'nr-stars');
    wrapper.setAttribute('role', 'img');
    wrapper.setAttribute('aria-label', interpolate(labels.rating_aria, { rating: number.format(value) }) || `${number.format(value)}/5`);
    for (let index = 0; index < 5; index += 1) {
      const star = element('span', 'nr-star');
      star.setAttribute('aria-hidden', 'true');
      // Clip each individual star; a row-width clip would include inter-star gaps.
      const fill = Math.max(0, Math.min(1, value - index)) * 100;
      star.style.setProperty('--nr-star-fill', `${fill}%`);
      star.append(element('span', 'nr-star__base', '★'), element('span', 'nr-star__fill', '★'));
      wrapper.append(star);
    }
    return wrapper;
  }
  function groupFor(data, productId) {
    // No substring matching, handle guess, or cross-product fallback.
    return Object.hasOwn(data.products, productId) ? data.products[productId] : null;
  }
  function summaryContent(group, labels = {}) {
    const fragment = document.createDocumentFragment();
    const countLabel = interpolate(labels[group.count === 1 ? 'count_one' : 'count_other'], { count: countFormat.format(group.count) });
    fragment.append(stars(group.average, labels), element('span', 'nr-summary__text', interpolate(labels.summary, { average: number.format(group.average), count_label: countLabel })));
    return fragment;
  }
  const dateKey = value => /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value || '') && Number.isFinite(Date.parse(value)) ? Date.parse(value) : null;
  const bucket = review => Math.min(5, Math.floor(review.rating));
  function inlineDocument(payload, productId) {
    if (payload && payload.products) return validate(payload);
    // Compatibility with an already confirmed original product metafield.
    // The old unconfirmed editor dataset is never used.
    if (!payload || payload.source_confirmed !== true || String(payload.product_id) !== productId || !Array.isArray(payload.reviews)) throw new Error('Unmatched inline reviews');
    const reviews = payload.reviews.map(row => ({ id: row.id, product_id: productId, name: row.name || row.author, body: row.body, rating: row.rating, title: row.title || '', date: row.date || '', verified_purchase: row.verified_purchase === true, images: row.images || [] }));
    const group = { count: reviews.length, average: reviews.reduce((sum, row) => sum + row.rating, 0) / reviews.length, reviews };
    return validate({ schema_version: 1, products: { [productId]: group } });
  }
  class NReviews extends HTMLElement {
    text(key, values = {}) { return interpolate(this.labels?.[key], values); }
    countLabel(count, customer = false) {
      const key = `${customer ? 'customer_' : ''}count_${count === 1 ? 'one' : 'other'}`;
      return this.text(key, { count: countFormat.format(count) });
    }
    summaryLabel(group) { return this.text('summary', { average: number.format(group.average), count_label: this.countLabel(group.count) }); }
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.hidden = true;
      this._abort = new AbortController();
      this.on = (node, event, listener) => node?.addEventListener(event, listener, { signal: this._abort.signal });
      try {
        this.labels = JSON.parse(this.querySelector('[data-review-i18n]')?.content.textContent || '{}');
        let data;
        if (this.dataset.source) data = await load(this.dataset.source);
        else data = inlineDocument(JSON.parse(this.querySelector('[data-reviews-json]')?.content.textContent || '{}'), this.dataset.productId);
        if (!this.isConnected) return;
        const group = groupFor(data, this.dataset.productId);
        if (!group) return;
        this.reviews = group.reviews.map((review, originalIndex) => ({ ...review, originalIndex }));
        this.options = new Map();
        this.querySelector('[data-review-images]')?.content.querySelectorAll('[data-review-image-id]').forEach(node => {
          if (!this.options.has(node.dataset.reviewImageId)) this.options.set(node.dataset.reviewImageId, { ...node.dataset });
        });
        this.visibleReviews = this.reviews.filter(review => this.options.get(review.id)?.enabled !== 'false');
        if (!this.visibleReviews.length) return;
        this.pageSize = Math.min(16, Math.max(4, Number(this.dataset.pageSize) || 8));
        this.limit = this.pageSize;
        this.filter = this.querySelector('[data-review-filter]');
        this.sort = this.querySelector('[data-review-sort]');
        this.grid = this.querySelector('[data-review-grid]');
        this.more = this.querySelector('[data-review-more]');
        this.querySelector('[data-review-average]').textContent = this.text('average', { average: number.format(group.average) });
        this.querySelector('[data-review-stars]').replaceChildren(stars(group.average, this.labels));
        this.querySelector('[data-review-count]').textContent = this.countLabel(group.count);
        this.renderDistribution();
        this.on(this.filter, 'change', () => { this.limit = this.pageSize; this.render(); });
        this.on(this.sort, 'change', () => { this.limit = this.pageSize; this.render(); });
        this.on(this.more, 'click', () => {
          const firstNew = this.grid.children.length;
          this.limit += this.pageSize;
          this.render();
          const card = this.grid.children[firstNew];
          if (card) { card.tabIndex = -1; card.focus({ preventScroll: true }); }
        });
        this.renderFeatured();
        this.render();
        this.querySelector('[data-reviews-content]').hidden = false;
        this.hidden = false;
        this.dataset.ready = 'true';
        const dialogLaunch = this.closest('[data-review-section]')?.querySelector('[data-open-reviews]');
        if (dialogLaunch) dialogLaunch.hidden = false;
        this.closest('[data-review-section]')?.querySelector('[data-review-editor-note]')?.setAttribute('hidden', '');
        this.updateSummaries(group);
        this.on(document, 'shopify:section:load', () => this.updateSummaries(group));
      } catch (_) { this.hidden = true; }
    }
    disconnectedCallback() {
      this._abort?.abort();
      this.initialized = false;
      this.dataset.ready = 'false';
      const dialogLaunch = this.closest('[data-review-section]')?.querySelector('[data-open-reviews]');
      if (dialogLaunch) dialogLaunch.hidden = true;
      document.querySelectorAll('[data-review-summary], [data-review-count-label]').forEach(link => {
        if (link.dataset.reviewProductId === this.dataset.productId) link.hidden = true;
      });
    }
    updateSummaries(group) {
      document.querySelectorAll('[data-review-summary]').forEach(link => {
        if (link.dataset.reviewProductId !== this.dataset.productId) return;
        link.href = `#${this.id}`;
        link.querySelector('[data-review-summary-stars]').replaceChildren(stars(group.average, this.labels));
        link.querySelector('[data-review-summary-label]').textContent = this.summaryLabel(group);
        link.hidden = false;
      });
      document.querySelectorAll('[data-review-count-label]').forEach(link => {
        if (link.dataset.reviewProductId !== this.dataset.productId) return;
        const label = link.querySelector('[data-review-count-text]');
        if (!label) return;
        link.href = `#${this.id}`;
        label.textContent = this.countLabel(group.count, true);
        link.hidden = false;
      });
    }
    renderDistribution() {
      const distribution = this.querySelector('[data-review-distribution]');
      distribution.replaceChildren();
      for (let rating = 5; rating >= 1; rating -= 1) {
        const count = this.reviews.filter(review => bucket(review) === rating).length;
        const button = element('button'); button.type = 'button';
        const text = rating === 5 ? this.text('distribution_five') : this.text('distribution_range', { from: rating, to: rating + 1 });
        const ratingLabel = rating === 5 ? this.text('filter_five') : this.text('filter_range', { from: rating, to: rating + 1 });
        button.setAttribute('aria-label', this.text('distribution_filter_aria', { rating_label: ratingLabel, count_label: this.countLabel(count) }));
        const bar = element('span', 'n-reviews__bar'); bar.setAttribute('aria-hidden', 'true');
        const fill = element('span'); fill.style.width = `${count / this.reviews.length * 100}%`; bar.append(fill);
        button.append(element('span', '', text), bar, element('span', '', countFormat.format(count)));
        this.on(button, 'click', () => { this.filter.value = String(rating); this.limit = this.pageSize; this.render(); });
        distribution.append(button);
      }
    }
    card(review) {
      const option = this.options.get(review.id) || {};
      const card = element('article', 'n-reviews__card'); card.dataset.reviewId = review.id;
      if (option.shopifyEditorBlock) card.setAttribute('data-shopify-editor-block', option.shopifyEditorBlock);
      const imageUrl = safeImage(option.image) || safeImage(review.images[0]);
      if (imageUrl && option.showImage !== 'false' && this.dataset.showImages !== 'false') {
        const image = element('img'); image.src = imageUrl; image.alt = option.alt || this.text('customer_photo_alt', { name: review.name });
        image.width = 600; image.height = 600; image.loading = 'lazy'; image.decoding = 'async';
        image.addEventListener('error', () => image.remove(), { once: true }); card.append(image);
      }
      const body = element('div', 'n-reviews__card-body');
      const head = element('div', 'n-reviews__card-head');
      head.append(element('h3', '', review.name), stars(review.rating, this.labels)); body.append(head);
      if (review.verified_purchase === true && this.dataset.showVerified !== 'false' && option.showVerified !== 'false') body.append(element('p', 'n-reviews__verified', this.text('verified_purchase')));
      if (review.title) body.append(element('h4', 'n-reviews__title', review.title));
      const quote = element('blockquote'); quote.append(element('p', '', review.body)); body.append(quote);
      if (review.date && this.dataset.showDates !== 'false') {
        const date = element('time', '', review.date);
        if (dateKey(review.date) != null) date.dateTime = review.date;
        body.append(date);
      }
      card.append(body); return card;
    }
    renderFeatured() {
      const track = this.querySelector('[data-review-featured-track]');
      if (!track) return;
      const featureCount = Math.min(12, Math.max(4, Number(this.dataset.featuredCount) || 6));
      const order = [...this.options.keys()].map(id => this.visibleReviews.find(review => review.id === id)).filter(Boolean);
      const seen = new Set(order.map(review => review.id));
      const featured = [...order, ...this.visibleReviews.filter(review => !seen.has(review.id))].slice(0, featureCount);
      track.replaceChildren(...featured.map(review => { const item = element('li'); item.append(this.card(review)); return item; }));
      const previous = this.querySelector('[data-review-featured-prev]');
      const next = this.querySelector('[data-review-featured-next]');
      const controls = this.querySelector('[data-review-featured-controls]');
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (controls) controls.hidden = max <= 2;
      };
      const step = direction => {
        const width = track.firstElementChild?.getBoundingClientRect().width || track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({ left: direction * (width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      this.on(previous, 'click', () => step(-1)); this.on(next, 'click', () => step(1));
      this.on(track, 'scroll', update);
      this.on(track, 'keydown', event => { if (event.target === track && ['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); step(event.key === 'ArrowRight' ? 1 : -1); } });
      const observer = new ResizeObserver(update); observer.observe(track);
      this._abort.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
      requestAnimationFrame(update);
    }
    render() {
      const rating = this.filter.value;
      const list = this.visibleReviews.filter(review => rating === 'all' || bucket(review) === Number(rating));
      const sort = this.sort.value;
      list.sort((a, b) => {
        if (sort === 'highest' || sort === 'lowest') {
          const difference = sort === 'highest' ? b.rating - a.rating : a.rating - b.rating;
          if (difference) return difference;
        }
        const aDate = dateKey(a.date), bDate = dateKey(b.date);
        if (aDate != null && bDate != null && aDate !== bDate) return sort === 'oldest' ? aDate - bDate : bDate - aDate;
        if (aDate != null && bDate == null) return -1;
        if (aDate == null && bDate != null) return 1;
        return a.originalIndex - b.originalIndex;
      });
      this.grid.replaceChildren(...list.slice(0, this.limit).map(review => this.card(review)));
      this.querySelector('[data-review-status]').textContent = list.length ? this.text('status', { visible: countFormat.format(Math.min(this.limit, list.length)), total: countFormat.format(list.length) }) : this.text('empty');
      this.more.hidden = list.length <= this.limit;
    }
  }
  window.NorelviNativeReviews = Object.freeze({ validate, groupFor, stars, safeImage });
  customElements.define('n-reviews', NReviews);
})();
